import { Timestamp } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { app } from './firebaseService';
import { CampLocationService } from './campLocationService';

// Types
export interface WrapperPatternContext {
  type: 'programs' | 'monitoring';
  locationId: string;
  organizationId: string;
}

export interface StoredWrapperPattern {
  pattern: string;
  description?: string;
  createdAt: Timestamp;
  confidence?: number;
  avgContentScore?: number;
}

export interface HtmlWrapperInferenceResult {
  success: boolean;
  patterns: Array<{
    pattern: string;
    description: string;
    urlsMatched: string[];
    confidence: number; // 0-1 score
    avgContentScore: number; // 0-1 score (for extraction: % of content extracted, for exclusion: % of content removed)
  }>;
  metrics: {
    totalUrls: number;
    successfulFetches: number;
    timeElapsedMs: number;
    avgPageSize: number;
  };
  error?: string;
}

export interface ValidationResult {
  isValid: boolean;
  errors: string[];
}

/**
 * Service for managing HTML wrapper patterns across different contexts
 */
export class SharedWrapperPatternService {
  /**
   * Load existing wrapper patterns for a given context
   */
  static async loadPatterns(context: WrapperPatternContext): Promise<StoredWrapperPattern[]> {
    if (!context.locationId) {
      throw new Error(`[SharedWrapperPatternService] loadPatterns called with empty locationId! Context: ${JSON.stringify(context)}`);
    }
    try {
      const configPath = context.type === 'programs' ? 'fetch' : 'monitor';
      const config = await CampLocationService.getConfig(context.locationId, configPath);

      if (!config) {
        console.log(`[SharedWrapperPatternService] No config found for ${context.type} context`);
        return [];
      }

      // Extract patterns from the appropriate config location
      const patterns = context.type === 'programs' 
        ? config.contentExtractionPatterns || []
        : config.contentExtractionPatterns || [];

      return patterns;
    } catch (error) {
      console.error('[SharedWrapperPatternService] Error loading patterns:', error);
      throw error;
    }
  }

  /**
   * Save wrapper patterns for a given context
   */
  static async savePatterns(
    context: WrapperPatternContext, 
    patterns: StoredWrapperPattern[]
  ): Promise<void> {
    if (!context.locationId) {
      throw new Error(`[SharedWrapperPatternService] savePatterns called with empty locationId! Context: ${JSON.stringify(context)}`);
    }
    try {
      console.log(`[SharedWrapperPatternService] Starting savePatterns with context:`, {
        type: context.type,
        locationId: context.locationId,
        organizationId: context.organizationId,
        patternCount: patterns.length
      });
      
      const configPath = context.type === 'programs' ? 'fetch' : 'monitor';
      console.log(`[SharedWrapperPatternService] Using config path: ${configPath}/_config`);
      
      // Log the patterns we're about to save
      console.log(`[SharedWrapperPatternService] Patterns to save:`, patterns);
      
      // First check if config exists
      const existingConfig = await CampLocationService.getConfig(context.locationId, configPath);
      console.log(`[SharedWrapperPatternService] Existing config:`, existingConfig);
      
      // Only update the contentExtractionPatterns array
      console.log(`[SharedWrapperPatternService] Calling updateConfig with patterns array...`);
      await CampLocationService.updateConfig(context.locationId, configPath, {
        contentExtractionPatterns: patterns
      });
      
      // Verify the save by reading back the config
      const updatedConfig = await CampLocationService.getConfig(context.locationId, configPath);
      console.log(`[SharedWrapperPatternService] Config after save:`, updatedConfig);
      
      if (!updatedConfig?.contentExtractionPatterns?.length) {
        console.warn(`[SharedWrapperPatternService] Warning: Saved patterns not found in updated config!`);
      } else {
        console.log(`[SharedWrapperPatternService] Successfully saved ${patterns.length} patterns for ${context.type} context`);
      }
    } catch (error) {
      console.error('[SharedWrapperPatternService] Error saving patterns:', error);
      throw error;
    }
  }

  /**
   * Infer wrapper patterns from a list of URLs
   */
  static async inferPatterns(
    urls: string[], 
    context: WrapperPatternContext
  ): Promise<HtmlWrapperInferenceResult> {
    try {
      console.log(`[SharedWrapperPatternService] Inferring patterns for ${urls.length} URLs`);
      console.log(`[SharedWrapperPatternService] URLs to analyze:`, urls);
      console.log(`[SharedWrapperPatternService] Context:`, context);
      
      // Initialize Firebase Functions with us-east4 region
      const functions = getFunctions(app, "us-east4");
      const inferPatterns = httpsCallable(functions, 'inferHtmlWrapper');

      // Prepare the payload for the cloud function
      const payload = {
        urls,
        locationId: context.locationId,
        locationName: context.type === 'programs' ? 'Programs Tab' : 'Monitoring Config',
        organizationId: context.organizationId,
        context: context.type
      };
      
      console.log(`[SharedWrapperPatternService] Sending payload to cloud function:`, payload);

      // Call the Cloud Function
      const result = await inferPatterns(payload);

      console.log(`[SharedWrapperPatternService] Cloud function response:`, result);
      const data = result.data as HtmlWrapperInferenceResult;
      
      if (data.success) {
        console.log(`[SharedWrapperPatternService] Successfully inferred ${data.patterns.length} patterns`);
        return data;
      } else {
        console.error('[SharedWrapperPatternService] Pattern inference failed:', data.error);
        return {
          success: false,
          patterns: [],
          metrics: {
            totalUrls: urls.length,
            successfulFetches: 0,
            timeElapsedMs: 0,
            avgPageSize: 0
          },
          error: data.error || 'Pattern inference failed'
        };
      }
    } catch (error) {
      console.error('[SharedWrapperPatternService] Error inferring patterns:', error);
      return {
        success: false,
        patterns: [],
        metrics: {
          totalUrls: urls.length,
          successfulFetches: 0,
          timeElapsedMs: 0,
          avgPageSize: 0
        },
        error: error instanceof Error ? error.message : 'Failed to infer patterns'
      };
    }
  }

  /**
   * Validate wrapper patterns
   */
  static validatePatterns(patterns: StoredWrapperPattern[]): ValidationResult {
    const errors: string[] = [];

    // Validate each pattern
    patterns.forEach((pattern, index) => {
      if (!pattern.pattern || typeof pattern.pattern !== 'string') {
        errors.push(`Pattern at index ${index} is invalid or missing`);
      }
      if (!pattern.createdAt) {
        errors.push(`CreatedAt timestamp missing for pattern at index ${index}`);
      }
    });

    return {
      isValid: errors.length === 0,
      errors
    };
  }

  /**
   * Remove duplicate patterns
   */
  static deduplicatePatterns(patterns: StoredWrapperPattern[]): StoredWrapperPattern[] {
    const uniquePatterns = new Map<string, StoredWrapperPattern>();

    patterns.forEach(pattern => {
      if (!uniquePatterns.has(pattern.pattern)) {
        uniquePatterns.set(pattern.pattern, pattern);
      }
    });

    return Array.from(uniquePatterns.values());
  }

  /**
   * Remove a wrapper pattern for a given context
   */
  static async removePattern(
    context: WrapperPatternContext,
    patternToRemove: string
  ): Promise<void> {
    if (!context.locationId) {
      throw new Error(`[SharedWrapperPatternService] removePattern called with empty locationId! Context: ${JSON.stringify(context)}`);
    }
    try {
      const configPath = context.type === 'programs' ? 'fetch' : 'monitor';
      const existingConfig = await CampLocationService.getConfig(context.locationId, configPath);
      if (!existingConfig || !existingConfig.contentExtractionPatterns) {
        return;
      }
      const updatedPatterns = existingConfig.contentExtractionPatterns.filter((p: any) => p.pattern !== patternToRemove);
      await CampLocationService.updateConfig(context.locationId, configPath, {
        contentExtractionPatterns: updatedPatterns
      });
    } catch (error) {
      console.error('[SharedWrapperPatternService] Error removing pattern:', error);
      throw error;
    }
  }
} 